package com.steamstreet.vegasful.web

import kotlinx.html.FlowContent
import kotlinx.html.span

/**
 * Class that runs some aspect of a client interaction
 */
interface ClientController {
    /**
     * Start this controller.
     */
    suspend fun run()
}

const val CONTROLLER_ATTRIBUTE = "data-account-module"

sealed class ClientControllerDefinition(val id: String) {
    fun install(content: FlowContent) {
        content.attributes[CONTROLLER_ATTRIBUTE] = id
    }
}

/**
 * Controller definition responsible for recording engagement with the entity whose page is being
 * shown: the visit itself, and any subsequent sign that the visitor went deeper than a glance.
 * Install this on entity detail pages only — a listing page is not a view of any one entity.
 */
object EntityViewedController : ClientControllerDefinition("entity-viewed") {
    const val pathAttribute = "data-entity-path"

    /**
     * The venue this entity belongs to, where it has one and is not itself a venue.
     *
     * Carried for analytics rather than for interactions: `event_viewed` and `ticket_link_tapped`
     * both report `venue_id`, and the client has no other way to know it — the page holds a path
     * and nothing else. Absent on venue pages, where the entity is the venue.
     */
    const val venueAttribute = "data-entity-venue"

    fun install(content: FlowContent, entityPath: String, venuePath: String? = null) {
        install(content)
        content.attributes[pathAttribute] = entityPath
        venuePath?.let { content.attributes[venueAttribute] = it }
    }
}

/**
 * Marks a region containing a link to buy tickets. A click inside it is recorded as a TICKETS
 * interaction against the entity path held in the attribute.
 */
const val TICKETS_LINK_ATTRIBUTE = "data-tickets-path"

/**
 * The venue a ticket link's entity belongs to. `ticket_link_tapped` is the conversion event and
 * reports `venue_id`, which is not derivable from an event path.
 */
const val TICKETS_VENUE_ATTRIBUTE = "data-tickets-venue"

/**
 * Marks a region containing a link that takes the visitor deeper into an entity's details, such as
 * the event's own site. A click inside it is recorded as a MORE_INFO interaction.
 */
const val MORE_INFO_LINK_ATTRIBUTE = "data-more-info-path"

/**
 * How many results a search-results fragment contains, written by the server onto the fragment's
 * root so that `search_performed` can report a real `results_count`.
 *
 * The alternative is the client counting anchors in markup it did not render, which would break
 * silently the next time the results layout changes.
 */
const val SEARCH_RESULT_COUNT_ATTRIBUTE = "data-search-result-count"

/**
 * Wrap content so that clicking it records an interaction against the given entity. The wrapper is
 * removed from layout, so it can be placed around a button or link without disturbing the design.
 * A null entity path renders the content unwrapped, for call sites where the link is only sometimes
 * worth recording.
 */
private fun FlowContent.interactionWrapper(
    attribute: String,
    entityPath: String?,
    venuePath: String? = null,
    content: FlowContent.() -> Unit
) {
    if (entityPath == null) {
        content()
        return
    }
    span {
        attributes[attribute] = entityPath
        venuePath?.let { attributes[TICKETS_VENUE_ATTRIBUTE] = it }
        attributes["style"] = "display: contents"
        content()
    }
}

/**
 * Wrap a ticket link so that clicking it is recorded as a TICKETS interaction.
 *
 * [venuePath] is optional and is used only for measurement — see [TICKETS_VENUE_ATTRIBUTE]. A
 * ticket link on a venue's own page does not need it; the client reads the venue off the entity.
 */
fun FlowContent.ticketsLink(entityPath: String?, venuePath: String? = null, content: FlowContent.() -> Unit) {
    interactionWrapper(TICKETS_LINK_ATTRIBUTE, entityPath, venuePath, content)
}

/**
 * Wrap a link to further detail so that clicking it is recorded as a MORE_INFO interaction.
 */
fun FlowContent.moreInfoLink(entityPath: String?, content: FlowContent.() -> Unit) {
    interactionWrapper(MORE_INFO_LINK_ATTRIBUTE, entityPath, content = content)
}